import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { ProcurementSettingsService } from '../../services/procurementSettings.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { ProcurementSettingsUpdateDTO } from '../../interfaces/procurement/procurementSettings.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { useToast } from '../../context/ToastContext';
import SupplierPortalSettingsCard from './supplierportal/SupplierPortalSettingsCard'; // F8

const EMPTY_DTO: ProcurementSettingsUpdateDTO = {
  inventoryAccountId: '',
  inventoryAdjustmentAccountId: '',
  grniAccountId: '',
  accountsPayableAccountId: '',
  inputTaxAccountId: '',
  poApprovalThreshold: 0,
  priceVarianceAccountId: '',
  consumptionAccountId: '',
  importClearingAccountId: '',
  customsPayableAccountId: '',
  advanceIncomeTaxAccountId: '',
  supplierAdvanceAccountId: '',
  priceVarianceTolerancePct: 0,
  taxVarianceTolerance: 1,
};

export const ProcurementSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<ProcurementSettingsUpdateDTO>(EMPTY_DTO);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setLoading(true);
      const [settings, accountsRes] = await Promise.all([
        ProcurementSettingsService.getByInstitution(institutionId).catch(() => null),
        AccountService.getAllAccounts(),
      ]);
      setAccounts(accountsRes ?? []);
      if (settings) {
        setForm({
          inventoryAccountId: settings.inventoryAccountId ?? '',
          inventoryAdjustmentAccountId: settings.inventoryAdjustmentAccountId ?? '',
          grniAccountId: settings.grniAccountId ?? '',
          accountsPayableAccountId: settings.accountsPayableAccountId ?? '',
          inputTaxAccountId: settings.inputTaxAccountId ?? '',
          poApprovalThreshold: settings.poApprovalThreshold ?? 0,
          priceVarianceAccountId: settings.priceVarianceAccountId ?? '',
          consumptionAccountId: settings.consumptionAccountId ?? '',
          importClearingAccountId: settings.importClearingAccountId ?? '',
          customsPayableAccountId: settings.customsPayableAccountId ?? '',
          advanceIncomeTaxAccountId: settings.advanceIncomeTaxAccountId ?? '',
          supplierAdvanceAccountId: settings.supplierAdvanceAccountId ?? '',
          priceVarianceTolerancePct: settings.priceVarianceTolerancePct ?? 0,
          taxVarianceTolerance: settings.taxVarianceTolerance ?? 1,
        });
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load procurement settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSaving(true);
      const saved = await ProcurementSettingsService.upsert(institutionId, form);
      toast.showSuccess('Procurement settings saved');
      if (saved?.warning) toast.showError(saved.warning);
    } catch (e) {
      toast.showApiError(e, 'Failed to save procurement settings');
    } finally {
      setSaving(false);
    }
  };

  // Group (header) accounts never receive postings, so only leaf accounts are offered.
  const postingAccounts = leafAccounts(accounts);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box data-help-page="procurement.settings.page" sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Procurement Settings</Typography>
      <Paper data-help-section="procurement.settings.section.main" sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.inventoryAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inventoryAccountId: id })}
          data-help="procurement.settings.field.inventoryAccount"
          label="Inventory Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.inventoryAdjustmentAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inventoryAdjustmentAccountId: id })}
          data-help="procurement.settings.field.inventoryAdjustmentAccount"
          label="Inventory Adjustment Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.consumptionAccountId ?? ''}
          onChange={(id) => setForm({ ...form, consumptionAccountId: id })}
          data-help="procurement.settings.field.consumptionAccount"
          label="Consumption Account (default)"
          placeholder="None"
          helperText="Debited when stock is issued or taken out (expense, or WIP for garments). An item category can override it. Without one, issues move stock but are not posted to the ledger."
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.supplierAdvanceAccountId ?? ''}
          onChange={(id) => setForm({ ...form, supplierAdvanceAccountId: id })}
          data-help="procurement.settings.field.supplierAdvanceAccount"
          label="Supplier Advance Account"
          placeholder="None"
          helperText="Asset account debited when an advance is paid to a supplier. Required to record advances."
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.grniAccountId ?? ''}
          onChange={(id) => setForm({ ...form, grniAccountId: id })}
          data-help="procurement.settings.field.grniAccount"
          label="GRNI Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.accountsPayableAccountId ?? ''}
          onChange={(id) => setForm({ ...form, accountsPayableAccountId: id })}
          data-help="procurement.settings.field.apAccount"
          label="Accounts Payable Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.inputTaxAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inputTaxAccountId: id })}
          data-help="procurement.settings.field.inputTaxAccount"
          label="Input Tax Account"
          placeholder="None"
        />

        {/* ---- W3-F import landed cost ---- */}
        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.importClearingAccountId ?? ''}
          onChange={(id) => setForm({ ...form, importClearingAccountId: id })}
          data-help="procurement.settings.field.importClearingAccount"
          label="Import Clearing Account"
          placeholder="None"
          helperText="Credited when a C&F agent's bill is capitalised through a bill of entry; the supplier bill's own clearing leg offsets it."
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.customsPayableAccountId ?? ''}
          onChange={(id) => setForm({ ...form, customsPayableAccountId: id })}
          data-help="procurement.settings.field.customsPayableAccount"
          label="Customs Payable Account"
          placeholder="None"
          helperText="Credited with duties and taxes assessed on a bill of entry, debited when the customs payment is made."
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.advanceIncomeTaxAccountId ?? ''}
          onChange={(id) => setForm({ ...form, advanceIncomeTaxAccountId: id })}
          data-help="procurement.settings.field.aitAccount"
          label="Advance Income Tax (AIT) Account"
          placeholder="None"
          helperText="Receivable debited with the AIT paid on imports; it is a recoverable tax credit, not a cost."
        />
        {/* ---- end W3-F ---- */}

        <TextField
          data-help="procurement.settings.field.poThreshold"
          label="PO Approval Threshold"
          type="number"
          value={form.poApprovalThreshold}
          onChange={e => setForm({ ...form, poApprovalThreshold: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'PO Approval Threshold' }}
          fullWidth
        />

        <AccountAutocomplete
          accounts={postingAccounts}
          value={form.priceVarianceAccountId ?? ''}
          onChange={(id) => setForm({ ...form, priceVarianceAccountId: id })}
          data-help="procurement.settings.field.priceVarianceAccount"
          label="Price Variance Account"
          placeholder="None"
        />

        <TextField
          data-help="procurement.settings.field.priceTolerance"
          label="Price difference allowed (%)"
          type="number"
          value={form.priceVarianceTolerancePct ?? 0}
          onChange={e => setForm({ ...form, priceVarianceTolerancePct: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'Price difference allowed', min: 0, step: 'any' }}
          helperText="A supplier bill whose unit price differs from the purchase order by more than this percentage is held for review."
          fullWidth
        />

        <TextField
          data-help="procurement.settings.field.taxTolerance"
          label="Tax difference allowed (amount)"
          type="number"
          value={form.taxVarianceTolerance ?? 1}
          onChange={e => setForm({ ...form, taxVarianceTolerance: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'Tax difference allowed', min: 0, step: 'any' }}
          helperText="A supplier bill whose tax differs from the expected tax by more than this amount is held for review. Set it to at least 1 so whole-taka VAT rounding does not block a bill."
          fullWidth
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button data-help="procurement.settings.save" variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      </Paper>
      <SupplierPortalSettingsCard /> {/* F8 */}
    </Box>
  );
};
